<div
    class="fixed bottom-0 top-0 right-0 w-full sm:w-[39rem] lg:h-screen lg:w-[var(--quick-chat-rail-width)] lg:shadow"
>
    <!--
        The "click anywhere on the shell to open the drawer" handler
        was removed (used to be `(click)="!opened && toggle()"` here
        and on the header below). Reason: the drawer-visibility
        watchdog in chat-drawer.component.ts auto-closes the drawer
        ~300ms after a force-open whenever there's no selected chat,
        no active overlay, and no transient creating state - so a
        bare click on the collapsed shell would briefly flash open
        and snap shut, looking broken. The drawer is now opened
        ONLY through intentional surfaces: the overlay shortcut
        icons in the side rail (Pricing / SMS / Archive / Notes /
        Email / Design / Quote / Gemini), selecting a chat
        avatar in the rail, or programmatic openers like
        QuickChatService.openChatPanel() / startChatWithEmail() /
        document.dispatchEvent('openQuickChat'). All of those
        carry their own "something to show" guarantee, so the
        watchdog never has cause to slam it shut on them.
    -->
    <div
        class="quick-chat-drawer-shell bg-card flex h-full w-full flex-col transition-opacity duration-400 ease-out sm:w-[39rem] lg:opacity-100"
        [ngClass]="{
            'shadow quick-chat-overlay-open max-lg:visible max-lg:opacity-100': opened,
            'max-lg:invisible max-lg:pointer-events-none max-lg:opacity-0': !opened,
        }"
    >
        <button
            class="quick-chat-close-tab"
            type="button"
            (click)="close(); $event.stopPropagation()"
            aria-label="Close quick chat"
            title="Close chat"
        >
            <mat-icon [svgIcon]="'heroicons_outline:chevron-right'"></mat-icon>
        </button>

        <!-- Header -->
        <div
            class="quick-chat-header flex flex-0 items-center justify-start"
        > 
            <!-- Overlay header (Pricing / Notepad / Email / Quote / Archive / SMS) -->
            @if (opened && activeOverlayHeader) {
                <div class="ml-3 flex flex-auto items-center">
                    <div class="relative flex h-10 w-10 flex-0 items-center justify-center">
                        @if (activeOverlayHeader.backAction) {
                            <button
                                type="button"
                                class="flex h-full w-full cursor-pointer items-center justify-center rounded-full text-white transition-transform hover:scale-105"
                                [ngClass]="activeOverlayHeader.badgeBg || 'bg-slate-600'"
                                (click)="backOverlayPanel(); $event.stopPropagation()"
                                aria-label="Back to inbox"
                                title="Back to inbox"
                            >
                                <mat-icon
                                    class="icon-size-5 text-white"
                                    [svgIcon]="activeOverlayHeader.icon"
                                ></mat-icon>
                            </button>
                        } @else {
                            <div
                                class="flex h-full w-full items-center justify-center rounded-full text-white"
                                [ngClass]="activeOverlayHeader.badgeBg || 'bg-slate-600'"
                            >
                                <mat-icon
                                    class="icon-size-5 text-white"
                                    [svgIcon]="activeOverlayHeader.icon"
                                ></mat-icon>
                            </div>
                        }
                    </div>
                    <div class="ml-4 truncate text-lg font-medium leading-5">
                        {{ activeOverlayHeader.name }}
                    </div>
                    <button
                        class="ml-auto mr-4"
                        type="button"
                        mat-icon-button
                        (click)="closeOverlayOrPanel(); $event.stopPropagation()"
                        aria-label="Close overlay"
                    >
                        <mat-icon
                            [svgIcon]="'heroicons_outline:x-mark'"
                        ></mat-icon>
                    </button>
                </div>
            }

            <!-- Toggle / "Quick Chat" default -->
            @if (!opened || (opened && !selectedChat && !activeOverlayHeader)) {
                <div class="flex flex-auto items-center justify-center">
                    <div class="flex w-16 flex-0 items-center justify-center">
                        <mat-icon
                            class="icon-size-6"
                            [svgIcon]="
                                'heroicons_outline:chat-bubble-left-right'
                            "
                        ></mat-icon>
                    </div>
                    <div class="text-secondary text-lg font-medium">
                        Quick Chat
                    </div>
                    <button
                        class="ml-auto mr-4"
                        type="button"
                        mat-icon-button
                        (click)="close(); $event.stopPropagation()"
                        aria-label="Close quick chat"
                    >
                        <mat-icon
                            [svgIcon]="'heroicons_outline:x-mark'"
                        ></mat-icon>
                    </button>
                </div>
            }

            <!-- Contact info (chat selected, no overlay) -->
            @if (opened && selectedChat && !activeOverlayHeader) {
                <div class="ml-3 flex flex-auto items-center">
                    <div
                        class="relative flex h-10 w-10 flex-0 items-center justify-center"
                    >
                        @if (_quickChatService.isDesignRequestChat(selectedChat) && !selectedChat.isGroup) {
                            <!-- Design Request: participant avatar with a palette badge -->
                            <div class="relative flex h-full w-full items-center justify-center rounded-full uppercase">
                                @if (selectedChat.contact?.avatar) {
                                    <img
                                        class="h-full w-full rounded-full object-cover"
                                        [src]="selectedChat.contact.avatar"
                                        [alt]="selectedChat.contact.name || 'Design request participant'"
                                    />
                                } @else {
                                    <div
                                        class="flex h-full w-full items-center justify-center rounded-full text-lg font-semibold uppercase"
                                        [ngClass]="[
                                            _quickChatService.getTwilioChatColor(selectedChat).bg,
                                            _quickChatService.getTwilioChatColor(selectedChat).text
                                        ]"
                                    >
                                        {{ (selectedChat.contact?.name || '?').charAt(0) }}
                                    </div>
                                }
                                <div
                                    class="absolute -bottom-0.5 -left-0.5 flex h-5 w-5 items-center justify-center rounded-full shadow-md ring-1 ring-white dark:ring-gray-800"
                                    [ngClass]="_quickChatService.getDesignRequestColor(selectedChat).bg"
                                >
                                    <mat-icon
                                        class="icon-size-3.5"
                                        [ngClass]="_quickChatService.getDesignRequestColor(selectedChat).text"
                                        svgIcon="mat_solid:palette"
                                    ></mat-icon>
                                </div>
                            </div>
                        } @else if (_quickChatService.isQuoteRequestChat(selectedChat) && !selectedChat.isGroup) {
                            <!-- Quote Request: participant avatar with a money badge -->
                            <div class="relative flex h-full w-full items-center justify-center rounded-full uppercase">
                                @if (selectedChat.contact?.avatar) {
                                    <img
                                        class="h-full w-full rounded-full object-cover"
                                        [src]="selectedChat.contact.avatar"
                                        [alt]="selectedChat.contact.name || 'Quote request participant'"
                                    />
                                } @else {
                                    <div
                                        class="flex h-full w-full items-center justify-center rounded-full text-lg font-semibold uppercase"
                                        [ngClass]="[
                                            _quickChatService.getTwilioChatColor(selectedChat).bg,
                                            _quickChatService.getTwilioChatColor(selectedChat).text
                                        ]"
                                    >
                                        {{ (selectedChat.contact?.name || '?').charAt(0) }}
                                    </div>
                                }
                                <div
                                    class="absolute -bottom-0.5 -left-0.5 flex h-5 w-5 items-center justify-center rounded-full shadow-md ring-1 ring-white dark:ring-gray-800"
                                    [ngClass]="_quickChatService.getDesignRequestColor(selectedChat).bg"
                                >
                                    <mat-icon
                                        class="icon-size-3.5"
                                        [ngClass]="_quickChatService.getDesignRequestColor(selectedChat).text"
                                        svgIcon="mat_solid:request_quote"
                                    ></mat-icon>
                                </div>
                            </div>
                        } @else if (_quickChatService.hasStackedHeaderAvatars(selectedChat)) {
                            <div class="relative h-full w-full">
                                @for (participant of _quickChatService.getStackedHeaderParticipants(selectedChat); track participant.email || participant.id || $index; let i = $index) {
                                    <div
                                        class="absolute flex h-8 w-8 items-center justify-center overflow-hidden rounded-full text-xs font-semibold uppercase ring-2 ring-card shadow-sm"
                                        [ngClass]="[
                                            _quickChatService.getGroupParticipantColor(i).bg,
                                            _quickChatService.getGroupParticipantColor(i).text,
                                            i === 0 ? 'left-0 top-0 z-[1]' : '-right-0.5 -bottom-0.5 z-[2]'
                                        ]"
                                        [matTooltip]="participant.name || participant.email">
                                        @if (participant.avatar) {
                                            <img class="h-full w-full object-cover" [src]="participant.avatar" [alt]="participant.name || 'Participant'"/>
                                        } @else {
                                            {{ (participant.name || participant.email || '?').charAt(0) }}
                                        }
                                    </div>
                                }
                            </div>
                        } @else if (chat.contact && chat.contact.avatar) {
                            <img
                                class="h-full w-full rounded-full object-cover"
                                [src]="chat.contact.avatar"
                                alt="Contact avatar"
                            />
                        } @else if (isSmsChat(chat)) {
                            <div class="relative flex h-full w-full items-center justify-center rounded-full text-lg font-semibold uppercase" 
                                 [ngClass]="[getSmsColor(chat.name).bg, getSmsColor(chat.name).text]">
                                {{ getSmsInitial(chat.name) }}
                                <!-- Small SMS badge in top-right corner -->
                                <div class="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full" [ngClass]="getSmsColor(chat.name).badgeBg">
                                    <mat-icon class="icon-size-3 text-white" svgIcon="mat_solid:sms"></mat-icon>
                                </div>
                            </div>
                        } @else if (chat.contact && chat.contact.name) {
                            <div
                                class="flex h-full w-full items-center justify-center rounded-full text-lg uppercase"
                                [ngClass]="[_quickChatService.getTwilioChatColor(chat).bg, _quickChatService.getTwilioChatColor(chat).text]"
                            >
                                {{ chat.contact.name.charAt(0) }}
                            </div>
                        } @else {
                            <div
                                class="flex h-full w-full items-center justify-center rounded-full bg-gray-300 text-lg uppercase text-gray-500 dark:bg-gray-600 dark:text-gray-300"
                            >
                                <mat-icon [svgIcon]="'heroicons_outline:user'"></mat-icon>
                            </div>
                        }
                    </div>
                    @if (chat.chatType === 'design' || chat.chatType === 'quote') {
                        <!-- Design/Quote Request: show friendlyName and participant name -->
                        <div class="ml-4 flex flex-col leading-tight">
                            <div class="text-lg font-medium truncate">
                                {{ chat.friendlyName || chat.name }}
                            </div>
                            @if (getParticipantSubtitle(chat); as participantSubtitle) {
                                <div class="text-sm text-gray-500 dark:text-gray-400">
                                    {{ participantSubtitle }}
                                </div>
                            }
                        </div>
                    } @else if (isSmsChat(chat)) {
                        <div class="ml-4 flex flex-col leading-tight">
                            <div class="text-lg font-medium truncate">
                                {{ getSmsCustomerName(chat.contact?.name || chat.name) }}
                            </div>
                            <div class="text-sm text-gray-500 dark:text-gray-400">
                                {{ getSmsPhoneNumber(chat.contact?.name || chat.name) }}
                            </div>
                        </div>
                    } @else {
                        <div class="ml-4 flex min-w-0 flex-col leading-tight">
                            <div class="truncate text-lg font-medium">
                                {{ chat.friendlyName || chat.contact?.name || chat.name }}
                            </div>
                            @if (chat.isGroup && getParticipantSubtitle(chat); as participantSubtitle) {
                                <div class="truncate text-sm text-gray-500 dark:text-gray-400">
                                    {{ participantSubtitle }}
                                </div>
                            }
                        </div>
                    }
                    @if (canStartQuickVideo) {
                        <button
                            class="quick-chat-video-launch ml-auto"
                            type="button"
                            mat-icon-button
                            (click)="startQuickVideo($event)"
                            [attr.aria-label]="'Start video call with ' + quickVideoParticipants.length + ' online participant' + (quickVideoParticipants.length === 1 ? '' : 's')"
                            [matTooltip]="'Video call ' + quickVideoParticipants.length + ' online participant' + (quickVideoParticipants.length === 1 ? '' : 's')"
                        >
                            <mat-icon>video_call</mat-icon>
                            <span class="quick-chat-video-launch__count">
                                {{ quickVideoParticipants.length }}
                            </span>
                        </button>
                    }
                    <button
                        class="mr-4"
                        [class.ml-auto]="!canStartQuickVideo"
                        type="button"
                        mat-icon-button
                        (click)="close(); $event.stopPropagation()"
                        aria-label="Close quick chat"
                    >
                        <mat-icon
                            [svgIcon]="'heroicons_outline:x-mark'"
                        ></mat-icon>
                    </button>
                </div>
            }
        </div>

        <!-- Content -->
         
        <div class="flex flex-auto border-t h-full" style="min-height: 0;">            
            <app-chat-drawer 
                [conversationId]="selectedChat?.id || lastSelectedChatId"
                [compactMode]="true"
                [navOnlyMode]="isOnChatPage"
            />
        </div>

    </div>
</div>

<!-- Select chat or start new template -->

<!-- Speech bubble tail SVG -->
<!-- prettier-ignore -->
<ng-template #speechBubbleExtension>
    <svg width="100%" height="100%" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <path d="M1.01522827,0.516204834 C-8.83532715,54.3062744 61.7609863,70.5215302 64.8009949,64.3061218 C68.8074951,54.8859711 30.1663208,52.9997559 37.5036011,0.516204834 L1.01522827,0.516204834 Z" fill="currentColor" fill-rule="nonzero"></path>
        </g>
    </svg>
</ng-template>
 

